import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../Hooks/useToast";
import { formatDateTimeForDisplay } from "../Utils/timeUtils";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import StarIcon from "@mui/icons-material/Star";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { Delete } from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import MainHeader from "../Components/Headres/Main_Header";
import DatagridTable from "../Components/Table/DataGrid_Table";
import { createColumn } from "../Components/Table/DataGrid_Cullums";
import CellMultiImg from "../Components/Table/Cell_MultiImg";
import EditableEmailCell from "../Components/Table/EditableEmailCell";
import AccoCodeModal from "./Reservations/ACC_Code_Modal";

import { FetchEditReservations, DeleteReservation } from "../Apis/Reservations/Reservations";
import { fetchReservations, updateReservationInStore } from "../Redux/Reservations_Slice";

const SearchSchema = Yup.object().shape({
  reservationNumber: Yup.string(),
  customerName: Yup.string(),
  customerPhone: Yup.string(),
  customerEmail: Yup.string(),
  status: Yup.string(),
  packageId: Yup.string(),
  userId: Yup.string(),
  dateOfReservation: Yup.date().nullable(),
  isAGift: Yup.string().oneOf(["true", "false"], "Ungültiger Status"),
  payWay: Yup.string()
});

const Homepage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [Loading, setIsLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [modalType, setModalType] = useState("code");
  
  // حالات التعديل
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [isUpdatingTime, setIsUpdatingTime] = useState(false);
  const [editFormData, setEditFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerStreet: "",
    customerPLZ: "",
    customerCity: "",
    status: "",
    adminNote: "",
    note: "",
    dateOfReservation: null,
    dateEndOfReservation: null
  });

  // حالات تأكيد الحذف
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);

  const { data, isLoading, error, pagination } = useSelector(
    (state) => state.Reservations
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // Since special reservations are now saved to database, we get them from server
  // Just sort data to show special reservations at the top
  const combinedData = React.useMemo(() => {
    // Ensure data is always an array
    const regularData = Array.isArray(data) ? data : [];
    
    // إزالة الترتيب الخاص - عرض الحجوزات حسب التاريخ فقط
    return [...regularData];
  }, [data, searchFilters]);

  // useEffect موحد للباجينيشن والفلاتر
  useEffect(() => {
    dispatch(
      fetchReservations({
        page: paginationModel.page + 1, // DataGrid يبدأ من 0، API يبدأ من 1
        limit: paginationModel.pageSize,
        filters: searchFilters
      })
    );
  }, [dispatch, paginationModel.page, paginationModel.pageSize, searchFilters]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleImageClick = (reservation) => {
    setSelectedReservation(reservation);
    
    const type = reservation.payWay === "paypal" ? "paypal" : "code";
    setModalType(type);
    
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReservation(null);
  };

  const handleAccept = async () => {
    if (!selectedReservation?.id) {
      toastError("Keine Reservierung ausgewählt!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await FetchEditReservations(
        { 
          isPaid: true,
          status: "accepted"
        },
        selectedReservation.id
      );
      
      if (response.status === 200 || response.status === 201) {
        toastSuccess("Reservierung erfolgreich angenommen!");
        handleCloseModal();
        
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        toastError(`Fehler beim Annehmen: Status ${response.status}`);
      }
    } catch (error) {
      toastError("Fehler beim Annehmen der Reservierung. Bitte erneut versuchen."); 
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDecline = async () => {
    if (!selectedReservation?.id) {
      toastError("Keine Reservierung ausgewählt!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await FetchEditReservations(
        { 
          isCanceled: true,
          isPaid: false,
          status: "rejected"
        },
        selectedReservation.id
      );
      
      if (response.status === 200 || response.status === 201) {
        toastSuccess("Reservierung erfolgreich abgelehnt!");
        handleCloseModal();
        
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Ablehnen: Status ${response.status}`;
        toastError(errorMsg);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Ablehnen der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage); 
    } finally {
      setIsLoading(false);
    }
  };

  // دالة فتح مودال تأكيد الحذف
  const handleDeleteClick = (reservation) => {
    setReservationToDelete(reservation);
    setDeleteDialogOpen(true);
  };

  // دالة إغلاق مودال تأكيد الحذف
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setReservationToDelete(null);
  };

  // دالة الحذف الفعلية
  const handleConfirmDelete = async () => {
    if (!reservationToDelete) return;

    setIsLoading(true);
    try {
      const response = await DeleteReservation(reservationToDelete.id);
      
      if (response.status === 200) {
        toastSuccess("Reservierung erfolgreich gelöscht!");
        handleCloseDeleteDialog();
        dispatch(
          fetchReservations({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Löschen: Status ${response.status}`;
        toastError(errorMsg);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Löschen der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // دوال التعديل
  const handleEdit = (reservation) => {
      console.log("🔍 EDIT: Opening edit dialog for reservation:", reservation.reservationNumber);
      console.log("🔍 EDIT: Original dateOfReservation:", reservation.dateOfReservation);
      console.log("🔍 EDIT: Original dateEndOfReservation:", reservation.dateEndOfReservation);
      
      setEditingReservation(reservation);
      
      // Convert UTC dates to local Date objects for DateTimePicker
      // DateTimePicker uses local time, so we create dates that display the same UTC time values
      let startDate = null;
      let endDate = null;
      
      if (reservation.dateOfReservation) {
        const utcDate = new Date(reservation.dateOfReservation);
        // Create a date using UTC components as local time values
        // This makes DateTimePicker display the same time as shown in the table (UTC)
        startDate = new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        );
      }
      
      if (reservation.dateEndOfReservation) {
        const utcDate = new Date(reservation.dateEndOfReservation);
        endDate = new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        );
      }
      
      console.log("🔍 EDIT: Converted startDate for picker:", startDate, startDate?.toISOString());
      console.log("🔍 EDIT: Converted endDate for picker:", endDate, endDate?.toISOString());
      
      setEditFormData({
          customerName: reservation.customerName || "",
          customerEmail: reservation.customerEmail || "",
          customerPhone: reservation.customerPhone || "",
          customerStreet: reservation.customerStreet || "",
          customerPLZ: reservation.customerPLZ || "",
          customerCity: reservation.customerCity || "",
          status: reservation.status || "",
          adminNote: reservation.adminNote || "",
          note: reservation.note || "",
          dateOfReservation: startDate,
          dateEndOfReservation: endDate
      });
      setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
      setEditDialogOpen(false);
      setEditingReservation(null);
      setEditFormData({
          customerName: "",
          customerEmail: "",
          customerPhone: "",
          customerStreet: "",
          customerPLZ: "",
          customerCity: "",
          status: "",
          adminNote: "",
          note: "",
          dateOfReservation: null,
          dateEndOfReservation: null
      });
  };

  const handleSaveEdit = async (payload) => {
    if (!editingReservation) return;
    setIsLoading(true);
    
    console.log("🔍 FRONTEND: Saving edit with payload:", payload);
    console.log("🔍 FRONTEND: Original reservation dates:", {
      dateOfReservation: editingReservation.dateOfReservation,
      dateEndOfReservation: editingReservation.dateEndOfReservation
    });
    
    // Convert dates to UTC ISO strings
    // DateTimePicker gives us local time, but we need to convert it back to UTC
    // by treating the local time values as UTC values
    let dateOfReservationUTC = payload.dateOfReservation;
    let dateEndOfReservationUTC = payload.dateEndOfReservation;
    
    if (payload.dateOfReservation instanceof Date) {
      // Create UTC date from local time components
      dateOfReservationUTC = new Date(Date.UTC(
        payload.dateOfReservation.getFullYear(),
        payload.dateOfReservation.getMonth(),
        payload.dateOfReservation.getDate(),
        payload.dateOfReservation.getHours(),
        payload.dateOfReservation.getMinutes(),
        payload.dateOfReservation.getSeconds()
      )).toISOString();
    }
    
    if (payload.dateEndOfReservation instanceof Date) {
      // Create UTC date from local time components
      dateEndOfReservationUTC = new Date(Date.UTC(
        payload.dateEndOfReservation.getFullYear(),
        payload.dateEndOfReservation.getMonth(),
        payload.dateEndOfReservation.getDate(),
        payload.dateEndOfReservation.getHours(),
        payload.dateEndOfReservation.getMinutes(),
        payload.dateEndOfReservation.getSeconds()
      )).toISOString();
    }
    
    const processedPayload = {
      ...payload,
      dateOfReservation: dateOfReservationUTC,
      dateEndOfReservation: dateEndOfReservationUTC
    };
    
    console.log("🔍 FRONTEND: Processed payload with ISO dates:", processedPayload);
    
    // Check if time is being changed
    const originalStart = editingReservation.dateOfReservation ? new Date(editingReservation.dateOfReservation).getTime() : null;
    const originalEnd = editingReservation.dateEndOfReservation ? new Date(editingReservation.dateEndOfReservation).getTime() : null;
    const newStart = processedPayload.dateOfReservation ? new Date(processedPayload.dateOfReservation).getTime() : null;
    const newEnd = processedPayload.dateEndOfReservation ? new Date(processedPayload.dateEndOfReservation).getTime() : null;
    
    const timeChanged = (originalStart && newStart && Math.abs(originalStart - newStart) > 60000) ||
                        (originalEnd && newEnd && Math.abs(originalEnd - newEnd) > 60000);
    
    if (timeChanged) {
      setIsUpdatingTime(true);
    }
    
    try {
      const reservationId = editingReservation?._id || editingReservation?.id;
      if (!reservationId) {
        console.error("❌ FRONTEND: Missing reservation id for update", editingReservation);
        toastError("Reservierungs-ID nicht gefunden. Bitte Seite neu laden und erneut versuchen.");
        setIsUpdatingTime(false);
        setIsLoading(false);
        return;
      }

      const API_URL = process.env.REACT_APP_API_URL || "https://api.vipwellness-suite.de/api/";
      console.log("📤 FRONTEND: Sending PUT request to:", `${API_URL}reservations/${reservationId}`);
      console.log("📤 FRONTEND: Request payload:", processedPayload);
      
      const response = await FetchEditReservations(processedPayload, reservationId);
      
      console.log("📥 FRONTEND: Response received:", {
        status: response.status,
        data: response.data,
        error: response.error
      });
      
      if (response.status === 200 || response.status === 201) {
        const serverReservation = response?.data?.data || {};
        const updatesForStore = {
          ...processedPayload,
          ...serverReservation,
          dateOfReservation: serverReservation.dateOfReservation || processedPayload.dateOfReservation,
          dateEndOfReservation: serverReservation.dateEndOfReservation || processedPayload.dateEndOfReservation
        };

        if (updatesForStore.customerName && !updatesForStore.userName) {
          updatesForStore.userName = updatesForStore.customerName;
        }

        dispatch(updateReservationInStore({
          id: reservationId,
          reservationNumber: editingReservation.reservationNumber,
          updates: updatesForStore
        }));

        setEditingReservation(prev => prev ? { ...prev, ...updatesForStore } : prev);
        
        console.log("✅ FRONTEND: Update successful, refreshing data...");
        
        // Wait a bit before closing to show success
        setTimeout(() => {
          toastSuccess("Reservierung erfolgreich aktualisiert!");
          setIsUpdatingTime(false);
          handleCloseEditDialog();
          
          // Refresh data after closing dialog
          dispatch(
            fetchReservations({
              page: paginationModel.page + 1,
              limit: paginationModel.pageSize,
              filters: searchFilters
            })
          );
        }, 1000);
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Aktualisieren: Status ${response.status}`;
        console.error("❌ FRONTEND: Update failed with status:", response.status, errorMsg);
        toastError(errorMsg);
        setIsUpdatingTime(false);
      }
    } catch (error) {
      console.error("❌ FRONTEND: Exception during update:", error);
      console.error("❌ FRONTEND: Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Aktualisieren der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage);
      setIsUpdatingTime(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
      setEditFormData(prev => ({
          ...prev,
          [field]: value
      }));
  };

  const handleSearch = (values) => {
    const filters = {};
    
    // تطبيق الفلاتر فقط إذا كانت لها قيمة
    if (values.reservationNumber && values.reservationNumber.trim()) {
      // البحث برقم الحجز - الأولوية الأولى
      const trimmedReservationNumber = values.reservationNumber.trim();
      filters.reservationNumber = trimmedReservationNumber;
      console.log("🔍 Frontend: Searching by reservation number:", trimmedReservationNumber);
    }
    
    if (values.customerName && values.customerName.trim()) {
      // البحث في اسم العميل
      const trimmedName = values.customerName.trim();
      filters.userName = trimmedName;
    }
    
    if (values.customerPhone && values.customerPhone.trim()) {
      // البحث في رقم الهاتف - إرسال الرقم كما هو للباك إند
      const trimmedPhone = values.customerPhone.trim();
      
      // إرسال الرقم كما هو - الباك إند سيتعامل مع جميع الأشكال
      filters.customerPhone = trimmedPhone;
    }
    
    if (values.customerEmail && values.customerEmail.trim()) {
      // البحث في البريد الإلكتروني
      const trimmedEmail = values.customerEmail.trim();
      filters.customerEmail = trimmedEmail;
    }
    
    if (values.status && values.status !== "") {
      filters.status = values.status;
    }
    
    if (values.payWay && values.payWay !== "") {
      filters.payWay = values.payWay;
    }
    
    if (values.dateOfReservation) {
      // تنسيق التاريخ للـ backend
      const date = new Date(values.dateOfReservation);
      filters.dateOfReservation = date.toISOString();
    }
    
    // تحديث الفلاتر فقط - useEffect سيتعامل مع الطلب
    if (filters.reservationNumber) {
      delete filters.userName;
      delete filters.customerPhone;
      delete filters.customerEmail;
    }
    setSearchFilters(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = (resetForm) => {
    resetForm();
    setSearchFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    
    // إعادة تحميل البيانات بدون فلاتر
    dispatch(
      fetchReservations({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: {}
      })
    );
  };

  const handleEmailUpdate = (reservationId, newEmail) => {
    // Refresh the data to show the updated email
    dispatch(
      fetchReservations({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: searchFilters
      })
    );
  };

  // Function to get row styling based on payment method
  const getRowClassName = (params) => {
    if (params.row.payWay === "code") {
      return "code-payment-row";
    } else if (params.row.payWay === "paypal") {
      return "paypal-payment-row";
    } else if (params.row.payWay === "special") {
      return "special-payment-row";
    }
    return "";
  };

  const columns = React.useMemo(() => [
    {
      ...createColumn("reservationNumber", "Buchungsnummer", 150),
      renderCell: (params) => (
        <Button
          onClick={() => navigate(`Reservations/ViewReservations/${params.row.id}`)}
          sx={{
            padding: 0,
            textTransform: "none",
            color: "#CD9D41",
            fontWeight: "bold"
          }}
        >
          {params.value}
        </Button>
      ),
    },
    {
      ...createColumn("customerName", "Kundenname", 200),
      renderCell: (params) => (
        <span style={{ fontWeight: "bold", color: "#333" }}>
          {params.value || params.row.userName || "N/A"}
        </span>
      ),
    },
    {
      ...createColumn("customerEmail", "E-Mail", 250),
      renderCell: (params) => (
        <EditableEmailCell
          value={params.value}
          reservationId={params.row.id}
          onEmailUpdate={handleEmailUpdate}
          disabled={true}
        />
      ),
    },
    createColumn("customerPhone", "Telefon", 150),
    {
      ...createColumn("createdAt", "Erstellt am", 180),
      renderCell: (params) => {
        if (!params.value) return "N/A";
        const date = new Date(params.value);
        
        // استخدام التوقيت المحلي بدلاً من UTC لإصلاح مشكلة نقص الساعات
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#CD9D41" }}>
              {hours}:{minutes}
            </Typography>
            <Typography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
              {day}.{month}.{year}
            </Typography>
          </Box>
        );
      }
    },
    {
      ...createColumn("dateOfReservation", "Datum der Reservierung", 200),
      renderCell: (params) => formatDateTimeForDisplay(params.value)
    },
    {
      ...createColumn("dateEndOfReservation", "Datum Ende der Reservierung", 250),
      renderCell: (params) => formatDateTimeForDisplay(params.value),
    },
    {
      ...createColumn("payWay", "Zahlungsart", 180),
      renderCell: (params) => {
        const payWay = params.value;
        let backgroundColor = "#f5f5f5";
        let color = "#333";
        let label = payWay;
        let icon = null;

        if (payWay === "code") {
          backgroundColor = "#ffebee";
          color = "#c62828";
          label = "Code";
        } else if (payWay === "paypal") {
          backgroundColor = "#ffffff";
          color = "#1976d2";
          label = "PayPal";
        } else if (payWay === "special") {
          backgroundColor = "#fff9c4";
          color = "#f57f17";
          label = "Sonderreservierung";
          icon = <StarIcon sx={{ fontSize: 14, mr: 0.5 }} />;
        }

        return (
          <span
            style={{
              backgroundColor,
              color,
              padding: "8px 12px",
              borderRadius: "8px",
              fontWeight: "bold",
              fontSize: "12px",
              display: "inline-flex",
              alignItems: "center",
              minWidth: "80px",
              textAlign: "center"
            }}
          >
            {icon}
            {label}
          </span>
        );
      },
    },
    {
      ...createColumn("isAGift", "Geschenk", 120),
      renderCell: (params) => {
        if (params.value) {
          return (
            <span
              style={{
                backgroundColor: "#FFE4E1",
                color: "#D2691E",
                padding: "8px 12px",
                borderRadius: "8px",
                fontWeight: "bold",
                fontSize: "12px"
              }}
            >
              🎁 Geschenk
            </span>
          );
        }
        return (
          <span
            style={{
              color: "#999",
              fontSize: "12px"
            }}
          >
            -
          </span>
        );
      },
    },
    createColumn("adminNote", "Admin-Notiz", 250),
    createColumn("note", "Kunden-Notiz", 250),
    createColumn("status", "Status", 200, "string", ({ value }) => {
      let color, label;
      if (value === "accepted" || value === "Angenommen") {
        color = "#B6E6C1";
        label = "Angenommen";
      } else if (value === "rejected" || value === "Abgelehnt") {
        color = "#E6B6B6";
        label = "Abgelehnt";
      } else {
        color = "#F0E0C0";
        label = "Ausstehend";
      }
      return (
        <span
          style={{
            backgroundColor: color,
            padding: "12px 18px",
            borderRadius: "10px",
            fontWeight: "bold"
          }}
        >
          {label}
        </span>
      );
    }),
    createColumn("codePhoto", "Bild", 100, "actions", (params) => (
      <CellMultiImg
        images={params.row.codePhotos}
        codePhoto={params.row.codePhoto}
        paymentProofPhoto={params.row.paymentProofPhoto}
        payWay={params.row.payWay}
        onClick={(imageData) => {
          handleImageClick({
            ...params.row,
            imageData
          });
        }}
        width="80px"
        height="60px"
      />
    )),
    // عمود الإجراءات الجديد مع التعديل والحذف - مصحح ليستخدم createColumn
    {
      ...createColumn("actions", "Aktionen", 150),
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton 
              onClick={() => handleEdit(params.row)} 
              color="primary"
              disabled={Loading}
              title="Reservierung bearbeiten"
              sx={{
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                }
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              onClick={() => handleDeleteClick(params.row)} 
              color="error"
              disabled={Loading}
              title="Reservierung löschen"
              sx={{
                '&:hover': {
                  backgroundColor: '#ffebee',
                }
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    }
  ], [navigate, Loading, searchFilters, paginationModel]);

  const Toolbar = () => (
    <Box sx={{ p: 2, bgcolor: "#D6B981" }}>
      <Formik
        initialValues={{ 
          reservationNumber: "", 
          customerName: "",
          customerPhone: "",
          customerEmail: "",
          status: "", 
          packageId: "", 
          userId: "", 
          dateOfReservation: null, 
          isAGift: "", 
          payWay: "" 
        }}
        validationSchema={SearchSchema}
        onSubmit={handleSearch}
      >
        {({ errors, touched, resetForm, setFieldValue, values, setErrors }) => (
          <Form style={{ display: "flex", alignItems: "center", gap: 20, width: "100%", justifyContent: "end" }}>
            <Field
              as={TextField}
              name="reservationNumber"
              label="Buchungsnummer"
              variant="outlined"
              size="small"
              placeholder="z.B. 12345"
              error={touched.reservationNumber && Boolean(errors.reservationNumber)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />

            <Field
              as={TextField}
              name="customerName"
              label="Kundenname"
              variant="outlined"
              size="small"
              placeholder="z.B. Max Mustermann"
              error={touched.customerName && Boolean(errors.customerName)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />

            <Field
              as={TextField}
              name="customerPhone"
              label="Telefon"
              variant="outlined"
              size="small"
              placeholder="z.B.  0123455678878"
              error={touched.customerPhone && Boolean(errors.customerPhone)}
              // helperText={touched.customerPhone && errors.customerPhone || "Alle deutschen Nummernformate unterstützt"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              name="customerEmail"
              label="E-Mail"
              variant="outlined"
              size="small"
              placeholder="z.B. kunde@example.com"
              error={touched.customerEmail && Boolean(errors.customerEmail)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              select
              name="status"
              label="Status"
              variant="outlined"
              size="small"
              error={touched.status && Boolean(errors.status)}
              helperText={touched.status && errors.status}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">Alle Status</MenuItem>
              <MenuItem value="accepted">Angenommen</MenuItem>
              <MenuItem value="rejected">Abgelehnt</MenuItem>
              <MenuItem value="pending">Ausstehend</MenuItem>
            </Field>
            <Field
              as={TextField}
              select
              name="payWay"
              label="Zahlungsart"
              variant="outlined"
              size="small"
              error={touched.payWay && Boolean(errors.payWay)}
              helperText={touched.payWay && errors.payWay}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">Alle Zahlungsarten</MenuItem>
              <MenuItem value="code">Code</MenuItem>
              <MenuItem value="paypal">PayPal</MenuItem>
              <MenuItem value="special">Sonderreservierung</MenuItem>
            </Field>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              slotProps={{ textField: { size: 'small' } }}      
              disableTime
              label="Reservierungsdatum"
              value={values.dateOfReservation}
              ampm={false}
              format="dd.MM.yyyy"
              onChange={(newValue) => {
                if (newValue) {
                  const normalizedDate = new Date(newValue);
                  normalizedDate.setHours(6, 0, 0, 0); 
                  setFieldValue("dateOfReservation", normalizedDate);
                } else {
                  setFieldValue("dateOfReservation", null);
                }
                if (errors.dateOfReservation) {
                  setErrors({ ...errors, dateOfReservation: undefined });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{height:10}}
                  error={touched.dateOfReservation && Boolean(errors.dateOfReservation)}
                  helperText={touched.dateOfReservation && errors.dateOfReservation}
                />
              )}
            />
            </LocalizationProvider>
            <Button type="submit" variant="contained" color="inherit" size="medium">
              Suchen
            </Button>
            <Button
            sx={{fontSize:"12px",px:3}}
              type="button"
              variant="outlined"
              color="inherit"
              size="medium"
              onClick={() => handleReset(resetForm)}
            >
              Aktualisieren
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );

  // مودال التعديل
  const EditDialog = () => {
    const [localFormData, setLocalFormData] = React.useState(editFormData);
    React.useEffect(() => {
      if (editDialogOpen) {
        setLocalFormData(editFormData);
      }
    }, [editDialogOpen, editFormData]);
    
    // Access isUpdatingTime from parent scope

    const updateField = (field, value) => {
      setLocalFormData(prev => ({ ...prev, [field]: value }));
    };

    const SectionCard = ({ title, subtitle, children }) => (
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          backgroundColor: '#fff',
          boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: subtitle ? 1.5 : 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6b5b3e' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: '#9c8b68' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {isUpdatingTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#D6B981' }}>
              <CircularProgress size={18} sx={{ color: '#D6B981' }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Aktualisierung...
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            opacity: isUpdatingTime ? 0.55 : 1,
            pointerEvents: isUpdatingTime ? 'none' : 'auto',
            transition: 'opacity 0.2s ease'
          }}
        >
          {children}
        </Box>
      </Box>
    );

    const handleLocalSave = () => {
      console.log("🔍 FRONTEND EditDialog: Saving with localFormData:", localFormData);
      console.log("🔍 FRONTEND EditDialog: dateOfReservation type:", typeof localFormData.dateOfReservation, localFormData.dateOfReservation);
      console.log("🔍 FRONTEND EditDialog: dateEndOfReservation type:", typeof localFormData.dateEndOfReservation, localFormData.dateEndOfReservation);
      handleSaveEdit(localFormData);
    };

    return (
    <Dialog 
      open={editDialogOpen} 
      onClose={isUpdatingTime ? undefined : handleCloseEditDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
          position: 'relative'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
          color: '#fff',
          fontWeight: 'bold'
        }}
      >
        Reservierung bearbeiten Nr.: {editingReservation?.reservationNumber}
      </DialogTitle>
      <DialogContent sx={{ pt: '30px !important', px: 3, pb: 3, backgroundColor: '#faf7ef' }}>
        <SectionCard title="Kundeninformationen" subtitle="Personen- und Kontaktdaten des Gastes">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kundenname"
                value={localFormData.customerName}
                onChange={(e) => updateField('customerName', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={localFormData.customerEmail}
                onChange={(e) => updateField('customerEmail', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefonnummer"
                value={localFormData.customerPhone}
                onChange={(e) => updateField('customerPhone', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Straße und Hausnummer"
                value={localFormData.customerStreet}
                onChange={(e) => updateField('customerStreet', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postleitzahl"
                value={localFormData.customerPLZ}
                onChange={(e) => updateField('customerPLZ', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stadt"
                value={localFormData.customerCity}
                onChange={(e) => updateField('customerCity', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFormData.status}
                  label="Status"
                  onChange={(e) => updateField('status', e.target.value)}
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6D7B8' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D6B981' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C5A572' }
                  }}
                >
                  <MenuItem value="pending">Ausstehend</MenuItem>
                  <MenuItem value="accepted">Angenommen</MenuItem>
                  <MenuItem value="rejected">Abgelehnt</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin-Notiz"
                multiline
                rows={3}
                value={localFormData.adminNote}
                onChange={(e) => updateField('adminNote', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kunden-Notiz"
                multiline
                rows={3}
                value={localFormData.note}
                onChange={(e) => updateField('note', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Reservierungsdetails" subtitle="Start- und Endzeit der Buchung">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Startdatum und -zeit"
                  value={localFormData.dateOfReservation}
                  onChange={(newValue) => updateField('dateOfReservation', newValue)}
                  format="dd.MM.yyyy HH:mm"
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          '&:hover fieldset': { borderColor: '#D6B981' },
                          '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Enddatum und -zeit"
                  value={localFormData.dateEndOfReservation}
                  onChange={(newValue) => updateField('dateEndOfReservation', newValue)}
                  format="dd.MM.yyyy HH:mm"
                  ampm={false}
                  minDateTime={localFormData.dateOfReservation}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          '&:hover fieldset': { borderColor: '#D6B981' },
                          '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
            <Typography variant="caption" sx={{ color: '#856404', display: 'block', fontWeight: 'bold' }}>
              ⚠️ Hinweis: Wenn Sie das Datum oder die Uhrzeit ändern, wird automatisch eine E-Mail mit den neuen Zeiten an den Kunden gesendet.
            </Typography>
          </Box>
        </SectionCard>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, backgroundColor: '#fff', position: 'relative' }}>
        {isUpdatingTime && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10,
            borderRadius: '0 0 12px 12px'
          }} />
        )}
        <Button 
          onClick={handleCloseEditDialog} 
          variant="outlined"
          disabled={isUpdatingTime}
          sx={{
            borderColor: '#D6B981',
            color: '#6b5b3e',
            '&:hover': { borderColor: '#C5A572', backgroundColor: '#faf7ef' },
            position: 'relative',
            zIndex: 1
          }}
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleLocalSave} 
          variant="contained"
          disabled={Loading || isUpdatingTime}
          sx={{
            background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': { background: 'linear-gradient(135deg, #C5A572 0%, #B8A572 100%)' },
            position: 'relative',
            zIndex: 1,
            minWidth: 180
          }}
          startIcon={isUpdatingTime ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
        >
          {isUpdatingTime ? 'Wird gespeichert...' : 'Änderungen speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
  };

  // مودال تأكيد الحذف الجميل
  const DeleteConfirmDialog = () => (
    <Dialog 
      open={deleteDialogOpen} 
      onClose={handleCloseDeleteDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <WarningAmberIcon 
            sx={{ 
              fontSize: 80, 
              color: '#ff6b6b',
              mb: 2
            }} 
          />
        </Box>
        
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          Reservierung löschen?
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}>
          Sind Sie sicher, dass Sie die Reservierung 
          <strong style={{ color: '#CD9D41' }}> {reservationToDelete?.reservationNumber} </strong>
          von <strong>{reservationToDelete?.customerName || reservationToDelete?.userName}</strong> löschen möchten?
        </Typography>
        
        <Box sx={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7',
          borderRadius: 2,
          p: 2,
          mb: 3
        }}>
          <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'medium' }}>
            ⚠️ Diese Aktion kann nicht rückgängig gemacht werden!
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
        <Button 
          onClick={handleCloseDeleteDialog}
          variant="outlined"
          size="large"
          sx={{ 
            minWidth: 120,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium'
          }}
        >
          Abbrechen
        </Button>
        <Button 
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
          size="large"
          disabled={Loading}
          sx={{ 
            minWidth: 120,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'medium',
            backgroundColor: '#ff6b6b',
            '&:hover': {
              backgroundColor: '#ff5252'
            }
          }}
        >
          {Loading ? 'Wird gelöscht...' : 'Ja, löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const rowsMemo = React.useMemo(() => {
    return combinedData;
  }, [combinedData, searchFilters]);
  const toolbarMemo = React.useMemo(() => <Toolbar />, [searchFilters]);

  return (
    <Box>
      {/* Dashboard Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ 
          fontWeight: "bold", 
          color: "#333",
          mb: 1
        }}>
          Dashboard - Übersicht
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#666" }}>
          Willkommen im Verwaltungsbereich. Hier finden Sie eine Übersicht aller Reservierungen.
        </Typography>
      </Box>

      <MainHeader
        onNavigate={() => navigate("Reservations/AddBuchungen")}
        title="Neue Buchung"
        paginationModel={paginationModel}
        searchFilters={searchFilters}
      />
      


      <Box sx={{ height: 600, width: "100%", mt: 3 }}>
        <style>
          {`
            .MuiDataGrid-row.code-payment-row {
              background-color: #ffebee !important;
            }
            .MuiDataGrid-row.code-payment-row:hover {
              background-color: #ffcdd2 !important;
            }
            .MuiDataGrid-row.paypal-payment-row {
              background-color: #ffffff !important;
            }
            .MuiDataGrid-row.paypal-payment-row:hover {
              background-color: #f5f5f5 !important;
            }
            .MuiDataGrid-row.special-payment-row {
              background-color: #fff9c4 !important;
            }
            .MuiDataGrid-row.special-payment-row:hover {
              background-color: #fff59d !important;
            }
          `}
        </style>
        
       
        
        <DatagridTable
          columns={columns}
          rows={rowsMemo}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={pagination?.total || 0}
          pageSizeOptions={[5, 10, 25]}
          loading={isLoading}
          toolbarComponent={toolbarMemo}
          getRowClassName={getRowClassName}
          key={`table-${Object.keys(searchFilters).length}-${rowsMemo.length}-${JSON.stringify(searchFilters)}`} // إعادة بناء الجدول عند تغيير الفلاتر
        />
      </Box>
      <AccoCodeModal
        open={openModal}
        onClose={handleCloseModal}
        reservation={selectedReservation}
        onAccept={handleAccept}
        onDecline={handleDecline}
        isLoading={Loading}
        modalType={modalType}
      />

      <EditDialog />
      <DeleteConfirmDialog />
    </Box>
  );
};

export default Homepage;
